// "External radio" mode of the /radio page: a public station (BBC Radio 2
// first) played in place of the Soloist stream, with the TV app's radio
// look instead of the visualizers - the drifting mesh background tinted
// from the station's artwork, the ripple rings behind the logo card, a
// news ticker in the station's language, and a clock in the LISTENER's
// local time. See public/radio.html for the markup/CSS (ported from
// public/tv.html + tv.css) and main-radio.js for the 'B' key that toggles it.
//
// HLS: BBC serves HLS. Safari plays it natively in <audio>; everywhere else
// hls.js feeds MediaSource. The stream must send CORS headers for that -
// the Akamai edge does (Access-Control-Allow-Origin: *), the bbci.co.uk
// CloudFront host does not, which is why STATIONS uses the former.
import Hls from 'hls.js';
import { API_BASE } from './api-base.js';

// The station list: written into the page by the server from
// config/stations.json (see src/services/stationRegistry.js forClient for
// the shape - id, name, key, art, newsSet, dateLocale, streamUrl or
// streamResolver, info {type, inband, encodeDelayS, fallbackDelayS}). What
// each field does:
// - streamUrl: played as is (HLS through hls.js, anything else natively);
//   streamResolver: a server path answering {url} for tokenised streams.
// - info.inband: the track travels IN the stream as ID3 timed metadata
//   (hls.js turns it into metadata text-track cues that become active
//   exactly when that audio plays, so the title is in sync by construction).
// - info.encodeDelayS / fallbackDelayS: how far behind the antenna the
//   listener is, for stations whose API describes the antenna (BBC): hls.js
//   measures the distance to the live edge, encodeDelayS adds the encoder's
//   own lag; fallbackDelayS serves where hls.js is not in charge.
export const STATIONS = (typeof window !== 'undefined' && window.__STATIONS__) || {};

const INFO_POLL_MS = 10000;
// Grace after a track's announced end before it is dropped: the API's end
// offsets are a little early or late, and a flash of the station screen
// between two songs is worse than a couple of seconds of the old title.
const TRACK_END_GRACE_S = 3;

// Short words that are words, not acronyms ("LA TRAVERSEE").
const SHORT_WORDS = ['la','le','les','de','du','des','un','une','et','en','au','aux','ou','my','me','it','in','on','of','to','up','go','so','no','by','we','us','is','as','at','do','be','he','if','or','an','a','i','y'];

const NEWS_POLL_MS = 90000;
const NEWS_STATIC_MS = 15000;
const CLOCK_TICK_MS = 15000;

function lightenChannel(v) { return Math.round(v + (255 - v) * 0.55); }

// A station's logo is registered as a path on the Pi ('img/rfm-logo.png').
// Served by the Pi that resolves on its own; bundled into the TV app it
// would resolve against the app's own files, so it is made absolute here.
function artUrl(station) {
  const art = station.art || '';
  return /^(https?:)?\/\//.test(art) ? art : API_BASE + '/' + art.replace(/^\//, '');
}

// Un geste fait DANS le tiroir (ouvrir la poignée, taper une recherche, faire
// défiler les radios) n'est pas une demande d'écoute : il ne doit pas
// déclencher la lecture restée bloquée par le navigateur. Seul un choix de
// station le fait, et celui-là appelle play() lui-même, dans le geste.
function gestureAsksForSound(e) {
  const t = e && e.target;
  if (!t || !t.closest) return true;
  if (t.closest('.station')) return true;      // choisir une radio : oui
  return !t.closest('#sheet');                 // le reste du tiroir : non
}

export class ExternalRadio {
  // els: { layer, audio, art, artCard, waves, newsTicker, newsTrack, clock,
  //        clockTime, clockDate, stationName, status?, trackTitle?,
  //        trackArtist?, programme? }
  // status (optional) is the page's status pill: when the browser refuses
  // autoplay (a page opened straight on a station, before any gesture), it
  // says so and the first click/touch/key anywhere starts the sound.
  constructor(els, options) {
    this._els = els;
    this._options = options || {};
    // Le « décor » : le même écran de station, mais prêté à la radio
    // personnelle pour son mode classique (voir startDecor plus bas). Ses
    // minuteurs vivent à part de ceux d'une vraie station.
    this._decor = null;
    this._decorTimers = [];
    // The news band is the station's country's headlines; a viewer who does
    // not want them (the TV's own toggle) gets the station screen without it,
    // and nothing is fetched either.
    this._newsEnabled = this._options.news !== false;
    // On a TV there is no pointer and no keyboard: the wording of the
    // "sound is blocked" line has to name the remote instead.
    this._gestureHint = this._options.gestureHint || 'Tap anywhere to start the sound';
    this._blocked = false;
    this._unblockedAt = 0;
    const go = (e) => {
      if (!gestureAsksForSound(e)) return;
      if (!this._blocked || !this._station || this._paused) return;
      this._blocked = false;
      this._unblockedAt = performance.now();
      this._setStatus('');
      this._els.audio.play().catch(() => {});
    };
    this._unblock = go;
    window.addEventListener('click', go, true);
    window.addEventListener('touchend', go, true);
    window.addEventListener('keydown', go, true);
    this._station = null;
    this._hls = null;
    this._newsItems = [];
    this._newsIndex = 0;
    this._newsRunning = false;
    this._timers = [];
    this._paused = false;
    this._volume = 0.8;
    this._onPauseChange = null;
    this._els.audio.preload = 'none';
    this._inband = null;     // {artist,title,image,genre,firstSeenAt} from ID3 cues (stations with info.inband)
    this._bindMetadataTracks();
    this._infoData = null;   // latest /api/station/now answer
    this._infoHistory = [];  // {data, receivedAt, firstSeenAt} for stations without timing
    this._infoShownKey = null;
    this._shownTrack = null; // the segment currently displayed (see _selectTrack)
    this._onTrackChange = null;
    this._els.audio.preload = 'none';
  }

  get active() { return !!this._station; }
  // La station dont l'heure et les actualités sont affichées : une vraie
  // station quand il y en a une, sinon celle qui a emprunté le décor.
  _decorStation() { return this._station || this._decor; }
  _pushTimer(id) { (this._station ? this._timers : this._decorTimers).push(id); }
  get station() { return this._station; }
  // True while the browser is refusing to start the sound without a gesture.
  get blocked() { return this._blocked; }
  // True just after a gesture unblocked it - that same press must not also
  // be read as "pause" or "go back", which is what it would otherwise do.
  get justUnblocked() { return performance.now() - this._unblockedAt < 800; }
  // A gesture from outside this document (the TV wrapper forwards the
  // remote's first key press) counts as one too.
  unblock() { this._unblock(); }
  get paused() { return this._paused; }

  setVolume(v) {
    this._volume = v;
    this._els.audio.volume = v;
  }

  onPauseChange(fn) { this._onPauseChange = fn; }
  // fn(hasTrack): a track (artist + title) appeared or disappeared in the
  // delayed now-playing data - the page switches between the visualizer
  // (track) and the station screen (no track) on it.
  onTrackChange(fn) { this._onTrackChange = fn; }

  // L'ÉMISSION EN COURS, quand l'API de la station la donne. Elle existait
  // déjà en interne — _renderInfo la reçoit — mais rien ne l'exposait au
  // dehors, alors que c'est ce qu'affiche le centre de l'écran en mode radio
  // pour les stations qui ne disent pas de titre.
  get programme() { return (this._infoData && this._infoData.programme) || null; }

  get hasTrack() {
    const t = this._shownTrack;
    return !!(t && t.artist && t.title);
  }

  // Seconds between what the listener hears and the antenna.
  _audioLagS() {
    if (this._forcedLagS != null) return this._forcedLagS;
    const info = this._station && this._station.info || {};
    if (this._hls && Number.isFinite(this._hls.latency) && this._hls.latency > 0) {
      return this._hls.latency + (info.encodeDelayS || 0);
    }
    return info.fallbackDelayS || 25;
  }

  // The instant of antenna time the listener is hearing right now.
  _audioTimeMs() {
    return Date.now() - this._audioLagS() * 1000;
  }

  // Which of the API's recent segments the listener is hearing: the one
  // whose absolute window (programme start + offsets) contains the audio
  // instant. Null between songs / during speech.
  _selectTrack(data) {
    if (this._station && this._station.info && this._station.info.inband) {
      const t = this._inband;
      return t ? Object.assign({}, t, { _startMs: t.firstSeenAt }) : null;
    }
    if (!data) return null;
    if (!data.programme || !data.programme.start) return this._selectTrackByHistory();
    const progStart = Date.parse(data.programme.start);
    if (!Number.isFinite(progStart)) return this._selectTrackByHistory();
    const audioMs = this._audioTimeMs();
    const list = (data.recent && data.recent.length ? data.recent : (data.track ? [data.track] : []));
    let best = null;
    for (const seg of list) {
      if (!seg || seg.startOffsetS == null) continue;
      const startMs = progStart + seg.startOffsetS * 1000;
      // The announced end is an estimate: a live performance overran it by
      // minutes while the API still said "Now Playing". A segment the API
      // flags as playing therefore has no end here - it lasts until a newer
      // one starts. Past segments keep their end (plus a little grace) so
      // speech between songs shows no track.
      const endMs = seg.nowPlaying ? Infinity
        : (seg.endOffsetS != null ? progStart + (seg.endOffsetS + TRACK_END_GRACE_S) * 1000 : Infinity);
      if (audioMs >= startMs && audioMs <= endMs && (!best || startMs > best._startMs)) {
        best = Object.assign({}, seg, { _startMs: startMs });
      }
    }
    return best;
  }

  // The current (delayed) track in the Sonos state shape the visualizer
  // profile is built from - see sonos-visual-profile.js buildProfile. Null
  // when nothing identifiable is on (speech, jingles, between tracks). The
  // position is where the BBC says the track is (programme progress minus
  // the track's start offset, both at fetch time), advanced since.
  // Stations that only say "this is the current track" (no timing): the
  // polled answers are kept with their receipt time, and the one shown is
  // the newest at least one stream delay old - so the title flips when the
  // listener hears the change, not when the API announced it. The track's
  // start is taken as the moment it first appeared, which gives an
  // approximate position for the visualizer.
  _selectTrackByHistory() {
    const cutoff = Date.now() - this._audioLagS() * 1000;
    let chosen = null;
    for (let i = this._infoHistory.length - 1; i >= 0; i--) {
      if (this._infoHistory[i].receivedAt <= cutoff) { chosen = this._infoHistory[i]; break; }
    }
    if (!chosen) chosen = this._infoHistory[0];
    const t = chosen && chosen.data && chosen.data.track;
    if (!t || !t.artist || !t.title) return null;
    return Object.assign({}, t, { _startMs: chosen.firstSeenAt + this._audioLagS() * 1000 });
  }

  profileState() {
    if (!this._station || !this.hasTrack) return null;
    const t = this._shownTrack;
    let durationS = t.startOffsetS != null && t.endOffsetS != null ? Math.max(0, t.endOffsetS - t.startOffsetS) : 0;
    if (!durationS && t.durationS > 0) durationS = t.durationS;
    let positionS = t._startMs != null ? (this._audioTimeMs() - t._startMs) / 1000 : 0;
    if (t.startOffsetS == null) positionS = (Date.now() - t._startMs) / 1000; // history/in-band start is already in listener time
    if (durationS > 0) positionS = Math.min(positionS, durationS);
    return {
      roomName: this._station.name,
      state: {
        playbackState: this._paused ? 'PAUSED_PLAYBACK' : 'PLAYING',
        elapsedTime: Math.max(0, positionS),
        currentTrack: {
          title: t.title,
          artist: t.artist,
          album: '',
          duration: durationS,
          absoluteAlbumArtUri: t.image || null,
          uri: 'x-piradio:' + this._station.id + ':' + (t.id || '')
        }
      }
    };
  }

  start(station) {
    if (this._station && this._station.id === station.id) return;
    this.stop();
    this.stopDecor();
    this._station = station;
    this._paused = false;
    const els = this._els;
    els.layer.classList.add('visible');
    if (els.stationName) {
      // The name stays as the accessible label and as the phone's version of
      // this line; on a wide screen (TV, desktop) CSS swaps it for the
      // station's own logo - see .ext-station-name.has-logo in radio.html.
      els.stationName.textContent = station.name;
      // ...except for a station that asks not to be badged. This screen IS
      // judicael.com's own radio, so stamping its logo across the top of it
      // is a site labelling itself - the others are third-party stations
      // being identified, which is a different thing.
      if (station.hideLogo) {
        // Nothing at the top at all - not the logo, and not the name falling
        // back in its place either. Both say the same thing, and on this
        // station's own screen neither needs saying.
        els.stationName.style.removeProperty('--ext-station-logo');
        els.stationName.classList.remove('has-logo');
        els.stationName.hidden = true;
      } else {
        els.stationName.hidden = false;
        els.stationName.style.setProperty('--ext-station-logo', 'url("' + artUrl(station) + '")');
        els.stationName.classList.add('has-logo');
      }
    }
    els.art.style.backgroundImage = 'url("' + artUrl(station) + '")';
    if (els.artCard) els.artCard.classList.add('logo');
    this._tintFromArt(artUrl(station));
    els.waves.classList.add('visible');
    this._startAudio();
    this._startClock();
    this._startNews();
    this._startInfo();
  }

  // --- le décor prêté à la radio personnelle ---------------------------------
  //
  // Son mode classique (flèche gauche) montre la même chose qu'un écran de
  // station : les ondes derrière la pochette, le bandeau d'actualités, l'heure
  // et le nom de la radio. Mais RIEN d'autre - pas d'audio, c'est le lecteur du
  // flux personnel qui joue, et pas d'interrogation d'API de titres, c'est
  // Soloist qui les donne. Le centre reste à la pochette.
  startDecor(station) {
    if (this._station || !station) return;
    if (this._decor && this._decor.id === station.id) return;
    this.stopDecor();
    this._decor = station;
    const els = this._els;
    els.layer.classList.add('visible');
    els.waves.classList.add('visible');
    if (els.stationName) {
      // Le nom écrit, jamais le logo : cet écran EST celui de judicael.com,
      // et un site qui se tamponne son propre logo se présente à lui-même.
      els.stationName.hidden = false;
      els.stationName.textContent = station.name;
      els.stationName.classList.remove('has-logo');
      els.stationName.style.removeProperty('--ext-station-logo');
    }
    // NI ACTUALITÉS NI HEURE ICI. Sur une station externe, le bandeau du bas
    // fait partie de son écran d'attente : c'est ce qu'une radio montre entre
    // deux titres. Sur judicael.com, ce seraient des nouvelles du monde et une
    // horloge posées sur une écoute personnelle - elles n'ont rien à y faire.
    // Restent les ondes et le nom.
  }

  // Les ondes du décor prennent les couleurs de la pochette en cours, comme
  // celles d'un écran de station prennent celles de son logo.
  tintDecor(src) {
    if (!this._decor || !src) return;
    this._tintFromArt(src);
  }

  stopDecor() {
    if (!this._decor) return;
    this._decor = null;
    this._decorTimers.forEach(clearTimeout);
    this._decorTimers.forEach(clearInterval);
    this._decorTimers = [];
    this._newsRunning = false;
    const els = this._els;
    els.layer.classList.remove('visible');
    els.waves.classList.remove('visible');
    els.newsTicker.classList.remove('visible');
    els.clock.classList.remove('visible');
    if (els.newsTrack) els.newsTrack.innerHTML = '';
  }

  stop() {
    if (!this._station) return;
    this._station = null;
    this._timers.forEach(clearTimeout);
    this._timers.forEach(clearInterval);
    this._timers = [];
    this._newsRunning = false;
    this._stopAudio();
    const els = this._els;
    els.layer.classList.remove('visible');
    els.waves.classList.remove('visible');
    els.newsTicker.classList.remove('visible');
    els.clock.classList.remove('visible');
    this._infoData = null;
    this._infoHistory = [];
    this._infoShownKey = null;
    const had = this.hasTrack;
    this._shownTrack = null;
    this._renderInfo(null, null);
    if (had && this._onTrackChange) this._onTrackChange(false);
  }

  togglePause() {
    if (!this._station) return;
    // Same gesture as the one that lifted the autoplay block: it meant
    // "start", not "pause" - see the capture-phase listener above.
    if (performance.now() - this._unblockedAt < 800) return;
    if (this._paused) {
      this._paused = false;
      this._startAudio();
    } else {
      this._paused = true;
      this._stopAudio();
    }
    if (this._onPauseChange) this._onPauseChange(this._paused);
  }

  _setStatus(text) {
    const el = this._els.status;
    if (!el) return;
    el.textContent = text;
    el.classList.toggle('visible', !!text);
  }

  _play() {
    const p = this._els.audio.play();
    if (p && typeof p.catch === 'function') {
      p.catch((err) => {
        if (err && err.name === 'NotAllowedError') {
          this._blocked = true;
          this._setStatus(this._gestureHint);
        }
      });
    }
  }

  // --- audio -----------------------------------------------------------------
  _startAudio() {
    const station = this._station;
    if (station.streamResolver && !this._resolvedUrl) {
      // Signed stream: ask the server for the current URL, then play it.
      this._setStatus('Connecting to ' + station.name + '…');
      fetch(API_BASE + station.streamResolver)
        .then((res) => res.json())
        .then((d) => {
          if (this._station !== station || this._paused) return;
          if (!d || !d.url) { this._setStatus('Stream unavailable'); return; }
          this._resolvedUrl = d.url;
          this._startAudio();
        })
        .catch(() => this._setStatus('Stream unavailable'));
      return;
    }
    const audio = this._els.audio;
    // En https, jamais de flux http : il serait bloqué (contenu mixte). La
    // page passe donc la même adresse en https (voir worker/stations.js).
    const brute = this._resolvedUrl || station.streamUrl;
    // Sauf une adresse du réseau local (la radio du Pi, http://192.168…) :
    // elle n'a pas de https, et la page qui la joue est faite pour ça.
    const locale = /^http:\/\/(localhost|127\.|10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.|[^/]*\.local[:/])/i.test(String(brute));
    const url = !locale && typeof location !== 'undefined' && location.protocol === 'https:' ? String(brute).replace(/^http:\/\//i, 'https://') : brute;
    audio.volume = this._volume;
    this._setStatus('Connecting to ' + this._station.name + '…');
    audio.addEventListener('playing', () => { if (!this._blocked) this._setStatus(''); }, { once: true });
    const isHls = /\.m3u8(\?|$)/i.test(url);
    if (!isHls) {
      // Direct stream (Icecast MP3/AAC): the element plays it natively. A
      // fresh query string per attach defeats caches, as radio-player.js does.
      audio.src = url + (url.indexOf('?') === -1 ? '?' : '&') + 't=' + Date.now();
      this._play();
      return;
    }
    if (Hls.isSupported()) {
      this._hls = new Hls({ liveSyncDurationCount: 3, enableWorker: true });
      this._hls.on(Hls.Events.ERROR, (evt, data) => {
        if (!data.fatal) return;
        // Network or media fatal errors: hls.js can usually recover; if
        // not, a full restart a few seconds later re-attaches the stream.
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) this._hls.startLoad();
        else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) this._hls.recoverMediaError();
        else this._timers.push(setTimeout(() => { if (this._station && !this._paused) { this._stopAudio(); this._startAudio(); } }, 4000));
      });
      this._hls.loadSource(url);
      this._hls.attachMedia(audio);
      this._hls.on(Hls.Events.MANIFEST_PARSED, () => this._play());
    } else {
      audio.src = url; // Safari: native HLS
      this._play();
    }
  }

  _stopAudio() {
    this._blocked = false;
    this._setStatus('');
    this._resolvedUrl = null;
    this._inband = null;
    const audio = this._els.audio;
    if (this._hls) { this._hls.destroy(); this._hls = null; }
    audio.pause();
    audio.removeAttribute('src');
    audio.load();
  }

  // --- in-band metadata (ID3 in HLS) ------------------------------------------
  // hls.js (and Safari's native HLS) expose ID3 frames as cues on a
  // 'metadata' text track; a cue is active exactly while the audio it was
  // muxed with plays. Reading activeCues on cuechange therefore gives the
  // track in perfect sync with what the listener hears.
  _bindMetadataTracks() {
    const audio = this._els.audio;
    const tracks = audio.textTracks;
    if (!tracks) return;
    const onCueChange = (track) => () => {
      const cues = track.activeCues;
      if (!cues || cues.length === 0) return;
      const tags = {};
      for (let i = 0; i < cues.length; i++) {
        const v = cues[i].value;
        if (!v || !v.key) continue;
        if (v.key === 'WXXX') tags[v.key + ':' + (v.info || '')] = v.data;
        else tags[v.key] = typeof v.data === 'string' ? v.data : (v.data && v.data.toString ? v.data.toString() : '');
      }
      this._onInbandTags(tags);
    };
    const bind = (track) => {
      if (track.kind !== 'metadata' || track._piradioBound) return;
      track._piradioBound = true;
      track.mode = 'hidden';
      track.addEventListener('cuechange', onCueChange(track));
    };
    for (let i = 0; i < tracks.length; i++) bind(tracks[i]);
    tracks.addEventListener('addtrack', (e) => bind(e.track));
  }

  _onInbandTags(tags) {
    if (!this._station || !this._station.info || !this._station.info.inband) return;
    // "IN THIS WORLD" / "MOBY": some stations shout every frame.
    // Acronyms and stylised names keep their capitals (U2, NTM); everything
    // else that shouts is calmed down.
    const niceCase = (str) => (!str || str !== str.toUpperCase() || !/[A-Z]/.test(str)) ? str
      : str.replace(/[^\s,&\/()\-]+/g, (w) => ((w.length <= 2 && SHORT_WORDS.indexOf(w.toLowerCase()) === -1) || !/[AEIOUY\u00c0-\u00dd]/.test(w)) ? w : w.charAt(0) + w.slice(1).toLowerCase());
    const title = niceCase(tags.TIT2 || '');
    // "FARRUKO/GREEICY/STEVE AOKI": slashes separate credits in Fun Radio's
    // tags; comma form is what the catalogue lookups (TheAudioDB, Deezer,
    // lyrics) and cache keys expect.
    const artist = niceCase((tags.TPE1 || '').replace(/\s*\/\s*/g, ', '));
    const genre = (tags.TCON || '').toLowerCase();
    const image = tags['WXXX:artworkURL_600x600'] || tags['WXXX:artworkURL_400x400'] || null;
    // Only frames tagged as a song are a track. Between songs (ads, talk,
    // jingles) Fun Radio's frames carry the programme name as TIT2, the host
    // as TPE1 and the station logo as artwork, with NO TCON at all - so a
    // missing genre means "not a track", never "assume a song". The logo
    // check is a belt-and-braces guard for the same case.
    const isSong = genre === 'song' || genre === 'music';
    // (the M6 group's stations also send a "fallback" station picture when
    // a song has no cover: real song, no artwork - Deezer fills it later)
    const isStationLogo = /logo-fun-radio|\/logo/i.test(image || '');
    const isFallbackArt = /\/fallback\//i.test(image || '');
    const next = (title && artist && isSong && !isStationLogo) ? { id: artist + '|' + title, artist: artist, title: title, image: isFallbackArt ? null : image, genre: genre } : null;
    const prevKey = this._inband ? this._inband.id : null;
    const nextKey = next ? next.id : null;
    if (prevKey === nextKey) return;
    this._inband = next ? Object.assign({ firstSeenAt: Date.now(), nowPlaying: true }, next) : null;
    if (next && !next.image) this._fetchCover(this._inband);
  }

  // A song the stream named but did not picture: the server looks the cover
  // up (Deezer). Applied only if that song is still the one on air.
  _fetchCover(track) {
    fetch(API_BASE + '/api/cover?artist=' + encodeURIComponent(track.artist) + '&title=' + encodeURIComponent(track.title))
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        if (!d || !d.image || this._inband !== track) return;
        track.image = d.image;
        track.imageSource = 'deezer';
        if (d.durationS && !track.durationS) track.durationS = d.durationS;
        this._infoShownKey = null; // the render key ignores the image: force a repaint
      })
      .catch(() => {});
  }

  // --- look ------------------------------------------------------------------
  // Same technique as tv.js's extractMeshColors: the artwork downscaled to a
  // 2x2 canvas gives four soft blended colours for the mesh blobs, and their
  // average (lightened) tints the ripples, halo, ticker and clock. Pixels
  // can only be read from an image the page is allowed to read, and the Pi's
  // static files carry no CORS header of their own - so the tint always goes
  // through the art proxy, which does. Same origin or not, one path.
  _tintFromArt(src) {
    if (/^https?:/i.test(src) && src.indexOf(API_BASE + '/api/art-proxy') !== 0) {
      src = API_BASE + '/api/art-proxy?url=' + encodeURIComponent(src);
    }
    const img = new Image();
    img.onload = () => {
      try {
        // 2x2 quadrants for the four mesh blobs, but averaged over OPAQUE
        // pixels only: the station logos are cropped PNGs whose transparent
        // margin would otherwise pull every colour toward black.
        const N = 16;
        const c = document.createElement('canvas');
        c.width = N; c.height = N;
        const ctx = c.getContext('2d');
        ctx.drawImage(img, 0, 0, N, N);
        const d = ctx.getImageData(0, 0, N, N).data;
        const root = document.documentElement.style;
        const quads = [[0, 0, 0, 0], [0, 0, 0, 0], [0, 0, 0, 0], [0, 0, 0, 0]];
        for (let y = 0; y < N; y++) {
          for (let x = 0; x < N; x++) {
            const i = (y * N + x) * 4;
            if (d[i + 3] < 128) continue;
            const q = quads[(y < N / 2 ? 0 : 2) + (x < N / 2 ? 0 : 1)];
            q[0] += d[i]; q[1] += d[i + 1]; q[2] += d[i + 2]; q[3] += 1;
          }
        }
        let sr = 0, sg = 0, sb = 0, sn = 0;
        quads.forEach((q, k) => {
          const n = q[3] || 1;
          const r = Math.round(q[0] / n), g = Math.round(q[1] / n), b = Math.round(q[2] / n);
          if (q[3]) { sr += q[0]; sg += q[1]; sb += q[2]; sn += q[3]; }
          root.setProperty('--ext-mesh-c' + (k + 1), 'rgb(' + r + ',' + g + ',' + b + ')');
        });
        if (sn) {
          root.setProperty('--ext-r', lightenChannel(sr / sn));
          root.setProperty('--ext-g', lightenChannel(sg / sn));
          root.setProperty('--ext-b', lightenChannel(sb / sn));
        }
      } catch (e) { /* keep the defaults */ }
    };
    img.src = src;
  }

  // --- now playing (track + programme), delayed to match what is heard -----
  _startInfo() {
    const info = this._station.info;
    this._renderInfo(null, null);
    this._infoHistory = [];
    if (!info) return;
    const param = new URLSearchParams(location.search).get('bbclatency');
    this._forcedLagS = param != null && Number.isFinite(Number(param)) ? Number(param) : null;
    const poll = () => {
      fetch(API_BASE + '/api/station/now?id=' + encodeURIComponent(this._station.id))
        .then((res) => res.json())
        .then((data) => {
          this._infoData = data;
          const prev = this._infoHistory[this._infoHistory.length - 1];
          const key = data.track ? data.track.id + '|' + data.track.artist + '|' + data.track.title : '';
          const prevKey = prev && prev.data.track ? prev.data.track.id + '|' + prev.data.track.artist + '|' + prev.data.track.title : '';
          const firstSeenAt = prev && key === prevKey ? prev.firstSeenAt : Date.now();
          this._infoHistory.push({ data: data, receivedAt: Date.now(), firstSeenAt: firstSeenAt });
          const keepFrom = Date.now() - (this._audioLagS() + 60) * 1000;
          while (this._infoHistory.length > 1 && this._infoHistory[1].receivedAt <= keepFrom) this._infoHistory.shift();
        })
        .catch(() => {});
    };
    const show = () => {
      if (!this._infoData && !(info && info.inband)) return;
      const had = this.hasTrack;
      this._shownTrack = this._selectTrack(this._infoData);
      this._renderInfo(this._shownTrack, this._infoData && this._infoData.programme);
      if (this.hasTrack !== had && this._onTrackChange) this._onTrackChange(this.hasTrack);
    };
    poll();
    this._timers.push(setInterval(poll, INFO_POLL_MS));
    this._timers.push(setInterval(show, 1000));
  }

  get audioLagS() { return this._audioLagS(); }

  _renderInfo(track, prog) {
    const els = this._els;
    const key = (track ? track.id + '|' + track.artist + '|' + track.title : '') + '||' + (prog ? prog.pid : '');
    if (key === this._infoShownKey) return;
    this._infoShownKey = key;
    if (els.trackTitle) els.trackTitle.textContent = track && track.title ? track.title : '';
    if (els.trackArtist) els.trackArtist.textContent = track && track.artist ? track.artist : '';
    if (els.programme) {
      els.programme.textContent = prog
        ? [prog.title, prog.presenter].filter(Boolean).join(' · ')
        : '';
    }
    // The cover of the current track takes the card and tints the room;
    // without one (speech, or between tracks) the station's own artwork
    // comes back.
    const station = this._station;
    if (track && track.image) {
      els.art.style.backgroundImage = 'url("' + track.image + '")';
      if (els.artCard) { els.artCard.classList.add('square'); els.artCard.classList.remove('logo'); }
      // Cross-origin image: pixels can only be read through the art proxy.
      this._tintFromArt(API_BASE + '/api/art-proxy?url=' + encodeURIComponent(track.image));
    } else if (station) {
      els.art.style.backgroundImage = 'url("' + artUrl(station) + '")';
      if (els.artCard) { els.artCard.classList.remove('square'); els.artCard.classList.add('logo'); }
      this._tintFromArt(artUrl(station));
    }
  }

  // --- clock: the viewer's local time, formatted for the station's language ---
  _startClock() {
    const els = this._els;
    const locale = this._decorStation().dateLocale || undefined;
    const tick = () => {
      const now = new Date();
      els.clockTime.textContent = now.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
      els.clockDate.textContent = now.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });
    };
    tick();
    els.clock.classList.add('visible');
    this._pushTimer(setInterval(tick, CLOCK_TICK_MS));
  }

  // --- news ticker: one headline at a time, as on the TV page -----------------
  _startNews() {
    if (!this._newsEnabled) return;
    const els = this._els;
    this._newsItems = [];
    this._newsIndex = 0;
    els.newsTicker.classList.add('visible');
    const poll = () => {
      fetch(API_BASE + '/api/news?set=' + encodeURIComponent(this._decorStation().newsSet || 'fr'))
        .then((res) => res.json())
        .then((data) => { this._newsItems = data.items || []; })
        .catch(() => {});
    };
    poll();
    this._pushTimer(setInterval(poll, NEWS_POLL_MS));
    if (!this._newsRunning) this._showNextHeadline();
  }

  _showNextHeadline() {
    if (!this._decorStation()) { this._newsRunning = false; return; }
    this._newsRunning = true;
    const track = this._els.newsTrack;
    track.innerHTML = '';
    const el = document.createElement('div');
    el.className = 'ext-news-item';
    if (this._newsItems.length === 0) {
      const loading = document.createElement('div');
      loading.className = 'ext-news-title';
      loading.textContent = this._decorStation().newsLoading || 'Loading the news…';
      el.appendChild(loading);
      track.appendChild(el);
      this._pushTimer(setTimeout(() => this._showNextHeadline(), 5000));
      return;
    }
    const item = this._newsItems[this._newsIndex % this._newsItems.length];
    this._newsIndex = (this._newsIndex + 1) % this._newsItems.length;
    const source = document.createElement('div');
    source.className = 'ext-news-source';
    source.textContent = item.source;
    el.appendChild(source);
    const title = document.createElement('div');
    title.className = 'ext-news-title';
    title.textContent = item.title;
    el.appendChild(title);
    track.appendChild(el);
    this._pushTimer(setTimeout(() => {
      if (!this._decorStation()) { this._newsRunning = false; return; }
      const done = () => { el.removeEventListener('transitionend', done); this._showNextHeadline(); };
      el.addEventListener('transitionend', done);
      el.classList.add('exiting');
      // A browser that skips the transition (hidden tab) must not stall the ticker.
      this._pushTimer(setTimeout(() => { if (el.parentNode) done(); }, 1000));
    }, NEWS_STATIC_MS));
  }
}
